import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { profile } from "../mock/mock";
import { EASE } from "./Reveal";

const NAV = [
  { id: "story", label: "Story", full: "The Story" },
  { id: "learned", label: "Learnings", full: "What I've Learned" },
  { id: "work", label: "Work", full: "My Work" },
  { id: "thoughts", label: "Thoughts", full: "Thoughts & News" },
  { id: "contact", label: "Contact", full: "Let's Learn Together" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState("dark");
  const [active, setActive] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? window.scrollY / max : 0);
      setScrolled(window.scrollY > 8);
      const probe = 72;
      let t = "dark";
      let a = "";
      document.querySelectorAll("[data-theme]").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top <= probe && r.bottom > probe) {
          t = el.dataset.theme;
          if (el.id && el.id !== "top") a = el.id;
        }
      });
      setTheme(t);
      setActive(a);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = useCallback((id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.5 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const isDark = theme === "dark";
  const fg = isDark ? "text-[#F5F5F7]" : "text-[#050505]";
  const dim = isDark
    ? "text-[#98989D] hover:text-[#F5F5F7]"
    : "text-[#6E6E73] hover:text-[#050505]";

  return (
    <>
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: EASE, delay: 0.2 }}
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter] duration-700 ${
          scrolled ? (isDark ? "glass-dark" : "glass-light") : "bg-transparent"
        }`}
      >
        <div className={`max-w-[1440px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between ${scrolled ? "" : "mix-blend-difference"}`}>
          <button
            onClick={() => scrollTo("top")}
            className={`font-display text-[15px] tracking-[-0.02em] transition-colors duration-500 ${fg}`}
            data-testid="logo-home"
          >
            {profile.name}
          </button>

          <nav className="hidden md:flex items-center gap-9" data-testid="main-nav">
            {NAV.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`relative text-[13px] font-medium tracking-[-0.005em] transition-colors duration-400 ${
                  active === s.id ? fg : dim
                }`}
                data-testid={`nav-${s.id}`}
              >
                {active === s.id && (
                  <motion.span
                    layoutId="nav-active-dot"
                    className="absolute -left-3.5 top-1/2 -translate-y-1/2 w-[5px] h-[5px] rounded-full bg-terra"
                    transition={{ duration: 0.5, ease: EASE }}
                  />
                )}
                {s.label}
              </button>
            ))}
          </nav>

          <button
            className={`md:hidden w-10 h-10 -mr-2 flex items-center justify-center transition-colors duration-500 ${fg}`}
            onClick={() => setOpen(true)}
            aria-label="Menu"
            data-testid="mobile-menu-open"
          >
            <Menu size={20} strokeWidth={1.4} />
          </button>
        </div>

        {/* Reading progress */}
        <div className="absolute bottom-0 left-0 right-0 h-px overflow-hidden">
          <div
            className="h-full bg-terra origin-left will-change-transform"
            style={{ transform: `scaleX(${progress})` }}
            data-testid="scroll-progress"
          />
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="fixed inset-0 z-[60] bg-bg text-ink"
            data-testid="mobile-menu"
          >
            <div className="h-full flex flex-col">
              <div className="px-6 h-16 flex items-center justify-between">
                <span className="font-display text-[15px]">{profile.name}</span>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="w-10 h-10 -mr-2 flex items-center justify-center"
                  data-testid="mobile-menu-close"
                >
                  <X size={20} strokeWidth={1.4} />
                </button>
              </div>
              <nav className="flex-1 flex flex-col justify-center px-6 gap-5">
                {NAV.map((s, i) => (
                  <motion.button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}
                    className="text-left font-display text-[38px] tracking-[-0.03em]"
                    data-testid={`mobile-nav-${s.id}`}
                  >
                    {s.full}
                  </motion.button>
                ))}
              </nav>
              <div className="px-6 py-8">
                <Link to="/" onClick={() => setOpen(false)} className="text-[13px] text-dim">
                  {profile.name} · {new Date().getFullYear()}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
